/**
 * Typed access to the public Vite-time config the SPA needs.
 * Anything under VITE_* is visible in the browser bundle.
 */
interface ImportMetaEnv {
  readonly VITE_OIDC_AUTHORITY?: string;
  readonly VITE_OIDC_CLIENT_ID?: string;
  readonly VITE_OIDC_REDIRECT_URI?: string;
  readonly VITE_OIDC_POST_LOGOUT_REDIRECT_URI?: string;
  readonly VITE_OIDC_SILENT_REDIRECT_URI?: string;
  readonly VITE_OIDC_SCOPE?: string;
  readonly VITE_OIDC_PROVIDER?: "keycloak" | "stytch" | "workers-oauth";
  readonly VITE_DEV_MODE?: string;
  readonly VITE_E2E_MODE?: string;
  readonly VITE_API_BASE_URL?: string;
  readonly VITE_PRYBAR_ENABLED?: string;
  readonly VITE_DD_ENV?: string;
  readonly VITE_DD_APPLICATION_ID?: string;
  readonly VITE_DD_CLIENT_TOKEN?: string;
  readonly VITE_SEGMENT_WRITE_KEY?: string;
  readonly VITE_BRANCH_KEY?: string;
}

// biome-ignore lint/correctness/noUnusedVariables: ambient declaration merging with global ImportMeta
interface ImportMeta {
  readonly env: ImportMetaEnv;
}

const DEFAULT_DD_APPLICATION_ID = "bfcdeddc-a8ad-437e-90d8-1ac089f38cfd";
const DEFAULT_DD_CLIENT_TOKEN = "pub954129443a0bf025d5180956354cdb07";

/*
 * Branch Web SDK keys for the HealthOS app. Public by design: a Branch key is
 * a client key that ships in the bundle either way, which is why these sit
 * here in the same shape as the Datadog client token above rather than in a
 * secret store.
 *
 * Branch has no `env` parameter. The key IS the environment, so a build that
 * picks the wrong one writes real attribution into the test dataset or test
 * traffic into the live one, and neither is recoverable after the fact. Only a
 * production build takes the live key; staging and every other deployed build
 * take the test key, and local dev takes neither (see `branchKey` below).
 *
 * Links resolve on the custom domain `links.thrive.gl` (Branch fallback:
 * `d1oxd-alternate.app.link`), configured dashboard-side. Nothing here needs
 * the domain at runtime, so it is recorded rather than coded.
 */
const DEFAULT_BRANCH_LIVE_KEY = "key_live_bruSLLj2Id3MSk7xCHK0Nejlqwc5Nj80";
const DEFAULT_BRANCH_TEST_KEY = "key_test_pyCLQJa6PeWIVkYABJIYKjebAzj3Jvma";

export function readEnv() {
  const env = import.meta.env;
  const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost:5173";
  const useDatadogDefaults = !import.meta.env.DEV;
  // E2E mode forces dev bypass auth (no OIDC) and suppresses the demo panel.
  // Set via VITE_E2E_MODE=true in the Playwright webServer env.
  const e2eMode = env.VITE_E2E_MODE === "true";
  const ddEnv = env.VITE_DD_ENV ?? import.meta.env.MODE;
  return {
    authority: env.VITE_OIDC_AUTHORITY,
    clientId: env.VITE_OIDC_CLIENT_ID,
    redirectUri: env.VITE_OIDC_REDIRECT_URI ?? `${origin}/callback`,
    postLogoutRedirectUri: env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI ?? `${origin}/`,
    silentRedirectUri: env.VITE_OIDC_SILENT_REDIRECT_URI ?? `${origin}/silent-callback`,
    scope: env.VITE_OIDC_SCOPE ?? "openid profile email",
    provider: env.VITE_OIDC_PROVIDER ?? "keycloak",
    configured: e2eMode ? false : Boolean(env.VITE_OIDC_AUTHORITY && env.VITE_OIDC_CLIENT_ID),
    devMode: env.VITE_DEV_MODE === "true" || e2eMode,
    apiBaseUrl: env.VITE_API_BASE_URL ?? (import.meta.env.DEV ? "http://127.0.0.1:8000" : ""),
    prybarEnabled: env.VITE_PRYBAR_ENABLED !== "false",
    ddEnv,
    ddApplicationId:
      env.VITE_DD_APPLICATION_ID ?? (useDatadogDefaults ? DEFAULT_DD_APPLICATION_ID : undefined),
    ddClientToken:
      env.VITE_DD_CLIENT_TOKEN ?? (useDatadogDefaults ? DEFAULT_DD_CLIENT_TOKEN : undefined),
    segmentWriteKey: env.VITE_SEGMENT_WRITE_KEY,
    /*
     * Left unset in local dev and tests, so no attribution session is created,
     * the 79kB SDK chunk is never fetched, and a developer clicking around
     * cannot pollute either Branch dataset. Set VITE_BRANCH_KEY to the test
     * key to exercise the integration locally.
     */
    branchKey:
      env.VITE_BRANCH_KEY ??
      (import.meta.env.DEV
        ? undefined
        : ddEnv === "production"
          ? DEFAULT_BRANCH_LIVE_KEY
          : DEFAULT_BRANCH_TEST_KEY),
  };
}
